import React, { useMemo } from "react";
import _ from 'i18n';
import {Button, Col, Icon, Row, Spin, Tooltip} from "antd";
import {formatAmount, formatNumber} from "../../../../services/utils";
import {InfoCircleOutlined} from "@ant-design/icons";

export const eventTypes = [
  {legend: _('TRAININGS'), color: "#18A0FB", lineName: "formations"},
  {legend: _('CYCLES'), color: "#5F5DE8", lineName: "cycles"},
  {legend: _('CYCLES_ESSENTIAL'), color: "#14B7AD", lineName: "cycles_essential"},
  {legend: _('SEASONS'), color: "#F47F4D", lineName: "saisons"},
  {legend: _('MONTHLY_PREMIUM'), color: "#FB1E5B", lineName: "monthly_premium"},
  {legend: _('YEARLY_PREMIUM'), color: "#FFD433", lineName: "yearly_premium"},
  {legend: _('CONGRESS'), color: "#6D7F92", lineName: "congrès"},
];

const calculateRenewalsNumber = (data, startMonth, endMonth) => {
  if (!data || data.length === 0 || !startMonth || !endMonth) {
    return 0;
  }

  return data.slice(startMonth - 1, endMonth).reduce((totalRenewals, record) => {
    return totalRenewals + record.renewals;
  }, 0);
};

const calculateAnnualActiveNumber = (data, startMonth, endMonth) => {
  if (!data || data.length === 0 || !startMonth || !endMonth) {
    return 0;
  }

  return data.slice(startMonth - 1, endMonth).reduce((totalRenewals, record) => {
    return totalRenewals + record.yearly_premium_active;
  }, 0);
};

const EventLineChartLegendRow = ({
  legend,
  color,
  caNumber,
  registrantNumber,
  chartLinesToShow,
  updateChartLinesToShow,
  fetching,
  isError,
  lineName,
  sourceRegistrantNumber,
  sourceCARegistrantNumber,
  type,
  sourceData,
  chartData,
  frequency,
  showCompareAppTypeStats,
  showCompareAppTypeStats2,
  showCompareStatsForAppType,
  showCompareStats,
  activePremium,
  activePremiumCurrentYear,
  activePremiumPreviousYear,
  startMonth,
  endMonth,
}) => {
  const renewals = useMemo(() => {
    return sourceData && lineName === 'monthly_premium'
      ? calculateRenewalsNumber(sourceData, startMonth, endMonth)
      : 0;
  }, [sourceData, lineName, startMonth, endMonth]);

  const renewalsPrecYear = useMemo(() => {
    return chartData && lineName === 'monthly_premium'
      ? calculateRenewalsNumber(chartData, startMonth, endMonth)
      : 0;
  }, [chartData, lineName, startMonth, endMonth]);

  const annualActiveNumber = useMemo(() => {
    return sourceData && lineName === 'yearly_premium'
      ? calculateAnnualActiveNumber(sourceData, startMonth, endMonth)
      : 0;
  }, [sourceData, lineName, startMonth, endMonth]);

  const annualActiveNumberPrecYear = useMemo(() => {
    return chartData && lineName === 'yearly_premium'
      ? calculateAnnualActiveNumber(chartData, startMonth, endMonth)
      : 0;
  }, [chartData, lineName, startMonth, endMonth]);

  const handleButtonClick = () => {
    if (frequency === 'DAILY') {
      showCompareStatsForAppType(lineName);
    } else {
      updateChartLinesToShow(lineName);
    }
  };

  return (
    <Row className={'legend-row'}>
      <Col span={sourceRegistrantNumber !== null ? 5 : ((frequency === 'DAILY' && showCompareStats === false) ? 10 : 15)} className={(!chartLinesToShow.includes(lineName) ? 'opacity-50' : '') + ((lineName == 'congrès' || lineName == 'monthly_premium') ? ' flex items-center' : '')}>
        <span className={'dot'} style={{backgroundColor: color}}/>
        <span className={'legend-label'}>{legend}</span>
        {lineName == 'congrès' && (
          <Tooltip placement="topLeft" title={_('PAID_FREE_PACK_REGISTRANTS')} arrowPointAtCenter>
            <InfoCircleOutlined
              className="info-icon ml-1"
              style={{color: '#ffc107'}}
            />
          </Tooltip>
        )}
        {sourceData && lineName == 'monthly_premium' && type !== 'CA' && (
          <Tooltip placement="topLeft" title={renewals + ' renouvellements'} arrowPointAtCenter>
            <InfoCircleOutlined
              className="info-icon ml-1"
              style={{color: '#ffc107'}}
            />
          </Tooltip>
        )}
      </Col>
      {sourceRegistrantNumber !== null && (
        <Col span={2} className={'registrants-number flex'} style={{marginLeft:"6px"}}>
          {Math.round((((sourceCARegistrantNumber / 100) - (caNumber / 100)) / (caNumber / 100 == 0 ? sourceCARegistrantNumber / 100 : caNumber / 100)) * 100) > 0 ? (
            <React.Fragment>
              <img src="/img/icons/manage-BM/BM_plus.svg"/>
              <span className={'ml-1'} style={{color: '#02af8e', fontSize: '12px'}}>{Math.abs(parseFloat(((((sourceCARegistrantNumber / 100) - (caNumber / 100)) / (caNumber / 100 == 0 ? sourceCARegistrantNumber / 100 : caNumber / 100)) * 100).toFixed(2)))}% </span>
            </React.Fragment>
          ) : Math.round((((sourceCARegistrantNumber / 100) - (caNumber / 100)) / (caNumber / 100 == 0 ? sourceCARegistrantNumber / 100 : caNumber / 100)) * 100) < 0 ? (
            <React.Fragment>
              <img src="/img/icons/manage-BM/BM_moins.svg" className={'mr-1'}/>
              <span className={'mr-1'} style={{color: '#fe3745', fontSize: '12px'}}>{Math.abs(parseFloat(((((sourceCARegistrantNumber / 100) - (caNumber / 100)) / (caNumber / 100 == 0 ? sourceCARegistrantNumber / 100 : caNumber / 100)) * 100).toFixed(2)))}% </span>
            </React.Fragment>
          ) : null}
        </Col>
      )}

      {frequency === 'DAILY' && showCompareStats === false && (
        <Col span={5}>
          <Button
            type="primary"
            onClick={() => showCompareStatsForAppType(lineName)}
            disabled={fetching}
            style={{width: '100%'}}
            className={"compare-button " + (showCompareAppTypeStats ? 'active-button' : '')}
          >
            {_('COMPARE')}
          </Button>
        </Col>
      )}

      <Col span={4}
           className={'registrants-number flex ' + (!chartLinesToShow.includes(lineName) ? 'opacity-50' : '')}
           style={{paddingLeft: '20px', color: color, opacity: sourceRegistrantNumber !== null ? 0.7 : 1}}>
        {fetching === false && isError === false ? (
          lineName == 'monthly_premium' && frequency == 'MONTHLY' ?
            formatNumber(registrantNumber) + ' (' + (activePremiumPreviousYear ? activePremiumPreviousYear.activeMonthly : activePremium.activeMonthly) + ')' :
            (lineName == 'yearly_premium' && frequency == 'MONTHLY') ?
              formatNumber(registrantNumber) + ' (' + (activePremiumPreviousYear ? activePremiumPreviousYear.activeYearly : activePremium.activeYearly) + ')' :
              formatNumber(registrantNumber)
        ) : (
          <React.Fragment>
            <div className={'flex justify-center'}>
              <Spin indicator={<Icon type="loading" style={{ fontSize: 24 }} spin />} />
            </div>
          </React.Fragment>
        )}
      </Col>

      <Col
        span={4}
        className={'registrants-number flex ' + (!chartLinesToShow.includes(lineName) ? 'opacity-50' : '')}
        style={{justifyContent:"right", color: color, opacity: sourceRegistrantNumber !== null ? 0.7 : 1}}
      >
        {fetching === false && isError === false && formatAmount(caNumber)}
      </Col>

      {sourceRegistrantNumber !== null && (
        <React.Fragment>
          <Col span={4}
               className={'registrants-number flex ' + (!chartLinesToShow.includes(lineName) ? 'opacity-50' : '')}
               style={{paddingLeft: '20px', color: color}}>
            {fetching === false && isError === false && (
              sourceData && lineName == 'monthly_premium' && frequency == 'MONTHLY' ?
                formatNumber(sourceRegistrantNumber) + ' (' + activePremiumCurrentYear.activeMonthly + ')' :
                (sourceData && lineName == 'yearly_premium' && frequency == 'MONTHLY' ?
                  formatNumber(sourceRegistrantNumber) + ' (' + activePremiumCurrentYear.activeYearly + ')' :
                  formatNumber(sourceRegistrantNumber))
            )}
          </Col>
          <Col span={4} className={'registrants-number flex ' + (!chartLinesToShow.includes(lineName) ? 'opacity-50' : '')} style={{justifyContent:"right", color: color}}>
            {fetching === false && isError === false && <span>{formatAmount(sourceCARegistrantNumber)}</span>}
          </Col>
        </React.Fragment>
      )}
      <Col span={1}>
        <button type={"button"} className={'show-line-button'} onClick={handleButtonClick}>
          <img
            src={chartLinesToShow.includes(lineName) ? "/img/icons/event-stats/eye-2.svg" : "/img/icons/event-stats/eye-1.svg"}
            alt=""
          />
        </button>
      </Col>
    </Row>
  );
};

export default EventLineChartLegendRow;








// WEBPACK FOOTER //
// ./app/components/Admin/EventStats/EventRegistrantLinechart/EventLineChartLegendRow.js